import { useRef, useEffect, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useMyPlayer } from '../hooks/usePlayers'
import { useMatches, useActivityFeed } from '../hooks/useMatches'
import { useMyFollows } from '../hooks/useFollows'
import { useScoringAverage, useSegmentWinRate } from '../hooks/useGames'
import { getInitials } from '../utils/colors'

export default function HomePage({ onReady, onMatchSelect }) {
  const { user } = useAuth()
  const { data: myPlayer, isLoading: playerLoading } = useMyPlayer()
  const { data: matches, isLoading } = useMatches()
  const { data: follows, isLoading: followsLoading } = useMyFollows()
  const followedPlayerIds = useMemo(() => new Set(follows?.map((f) => f.id) || []), [follows])
  const { data: feedItems } = useActivityFeed(followedPlayerIds, !followsLoading)
  const { data: scoringAvg } = useScoringAverage(myPlayer?.id)
  const { data: winRate } = useSegmentWinRate(myPlayer?.id)

  // homeReady fires as soon as the primary data (matches list + my profile)
  // is available. Activity feed, scoring average, and win rate are non-blocking:
  // their cards render placeholders inline if the data isn't ready yet. This
  // prevents the full-screen loading overlay from hanging on resume when one
  // of the secondary queries is slow or failing.
  const ready = !playerLoading && !isLoading
  const readyRef = useRef(false)
  if (ready && !readyRef.current) {
    readyRef.current = true
  }
  useEffect(() => {
    if (readyRef.current) onReady?.()
  }, [ready])

  const meta = user?.user_metadata || {}
  const displayName = myPlayer?.display_name || meta.display_name || meta.full_name || meta.name || user?.email?.split('@')[0] || 'User'
  const avatarColor = myPlayer?.avatar_color || '#0ea47a'
  const initials = myPlayer ? getInitials(myPlayer) : displayName.charAt(0).toUpperCase()
  const handicap = myPlayer?.handicap_index

  const completedMatchCount = matches?.filter((m) => m.status === 'completed').length || 0
  const completedRoundCount = matches?.reduce((sum, m) => {
    if (m.match_type === 'team_match' || m.match_type === 'invitational') {
      return sum + (m.sessions?.filter((s) => s.status === 'completed').length || 0)
    }
    return sum + (m.games?.filter((g) => g.status === 'completed').length || 0)
  }, 0) || 0


  return (
    <div>
      {/* User profile header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.875rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: avatarColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#fff',
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {displayName}
          </div>
          <div style={{
            fontSize: '0.8125rem',
            color: 'var(--text-muted)',
            marginTop: '0.125rem',
          }}>
            {handicap != null && Number(handicap) !== 0
              ? `Handicap Index: ${handicap}`
              : 'No handicap set'}
          </div>
        </div>
      </div>

      {/* Stats badges */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', marginBottom: '1.5rem' }}>
        {[
          { value: completedMatchCount, label: 'Events\nCompleted' },
          { value: completedRoundCount, label: 'Rounds\nCompleted' },
          { value: scoringAvg ? scoringAvg.average : '—', label: 'Scoring\nAverage' },
          { value: winRate ? `${winRate.rate}%` : '—', label: 'Segment\nWin Rate' },
        ].map(({ value, label }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              boxShadow: 'var(--card-shadow, none)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.125rem',
              fontWeight: '700',
              color: 'var(--text-primary)',
            }}>
              {value}
            </div>
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: '500', textAlign: 'center', lineHeight: 1.3 }}>
              {label.split('\n').map((line, i) => <span key={i}>{i > 0 && <br />}{line}</span>)}
            </span>
          </div>
        ))}
      </div>

      {/* Activity Feed */}
      <div>
        <h3 style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.75rem',
        }}>
          Activity Feed
        </h3>
        {!feedItems?.length ? (
          <div style={{
            padding: '2rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.75rem',
            border: '1px solid var(--border-color)',
          }}>
            No activity yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {feedItems.map((match) => {
              const isTeamOrInvitational = match.match_type === 'team_match' || match.match_type === 'invitational'
              const isLive = match.status === 'active'
              const roundCount = isTeamOrInvitational
                ? (match.sessions?.filter((s) => s.status === 'completed').length || 0)
                : (match.games?.filter((g) => g.status === 'completed' || g.status === 'in_progress').length || 0)
              const playerCount = match.match_players?.length || 0
              const isOwner = match.owner_id === user?.id
              const isMember = match.match_players?.some((mp) => mp.user_id === user?.id)
              const ownerPlayer = match.match_players?.find((mp) => mp.user_id != null && mp.user_id === match.owner_id)?.player
              const ownerName = ownerPlayer?.display_name || 'Unknown'
              const eventType = match.match_type === 'team_match' ? 'a Team Match'
                : match.match_type === 'invitational' ? 'an Invitational'
                : match.match_type === 'trip' ? 'a Golf Trip'
                : 'a Round'
              const verb = isLive ? 'is playing' : 'completed'
              const cardTitle = (isOwner || isMember)
                ? (isLive ? `You're playing ${eventType}` : `You completed ${eventType}!`)
                : `${ownerName} ${verb} ${eventType}${isLive ? '' : '!'}`
              const dateStr = new Date(match.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })
              return (
                <button key={match.id} onClick={() => onMatchSelect?.(match.id)} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  padding: '0.75rem 2.25rem 0.75rem 1rem',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '0.75rem',
                  border: '1px solid var(--border-color)',
                  borderLeft: isLive ? '3px solid #10b981' : '1px solid var(--border-color)',
                  textAlign: 'left',
                  width: '100%',
                  cursor: 'pointer',
                  position: 'relative',
                }}>
                  {/* Top row: avatar + completed badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {(() => {
                      const isMe = isOwner || isMember
                      const aColor = isMe ? avatarColor : (ownerPlayer?.avatar_color || '#34d399')
                      const aInitials = isMe ? initials : (ownerPlayer ? getInitials(ownerPlayer) : ownerName.charAt(0).toUpperCase())
                      return (
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: aColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontSize: '0.6875rem',
                          fontWeight: '700',
                          color: '#fff',
                        }}>
                          {aInitials}
                        </div>
                      )
                    })()}
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '0.1875rem 0.5rem',
                      borderRadius: '9999px',
                      color: isLive ? '#10b981' : 'var(--text-muted)',
                      backgroundColor: isLive ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-tertiary)',
                    }}>
                      {cardTitle}
                    </span>
                  </div>
                  {/* Details */}
                  <div>
                    <div style={{ marginBottom: '0.125rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {match.match_name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                      <span>{dateStr}</span>
                      <span>&middot;</span>
                      <span>{roundCount} {roundCount === 1 ? 'round' : 'rounds'}</span>
                      <span>&middot;</span>
                      <span>{playerCount} {playerCount === 1 ? 'player' : 'players'}</span>
                    </div>
                  </div>
                  <ChevronRight size={18} style={{ color: 'var(--text-muted)', position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
